<template>
	<!-- 外层：绝对定位居中，不参与动画（始终渲染，不占空间不阻挡交互） -->
	<div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-9999 pointer-events-none">
		<!-- 内层：动画过渡，由 visible 控制显示/隐藏 -->
		<transition name="inline-message-fade">
			<div v-if="visible" class="inline-message-box" :class="messageClass">
				<span class="flex-shrink-0 text-xl leading-none">
					<el-icon v-if="currentType === 'success'" color="#50EE2C" size="24px">
						<CircleCheck />
					</el-icon>
					<el-icon v-else color="#F92863" size="24px">
						<Warning />
					</el-icon>
				</span>
				<span class="text-xl font-medium whitespace-nowrap">{{ currentText }}</span>
			</div>
		</transition>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = withDefaults(
	defineProps<{
		text?: string
		type?: 'success' | 'error'
		duration?: number
	}>(),
	{
		text: '',
		type: 'success',
		duration: 3000
	}
)

const emit = defineEmits<{
	close: []
}>()

const visible = ref(false)
const currentText = ref('')
const currentType = ref<'success' | 'error'>('success')
let timer: ReturnType<typeof setTimeout> | null = null

const messageClass = computed(() => (currentType.value === 'success' ? 'bg-[#303133] text-white' : 'bg-[#303133] text-white'))

function clearTimer() {
	if (timer !== null) {
		clearTimeout(timer)
		timer = null
	}
}

function startTimer(ms: number) {
	clearTimer()
	if (ms > 0) {
		timer = setTimeout(() => hide(), ms)
	}
}

function show(text: string, type?: 'success' | 'error', duration?: number) {
	currentText.value = text
	currentType.value = type ?? props.type
	visible.value = true
	startTimer(duration ?? props.duration)
}

function hide() {
	clearTimer()
	visible.value = false
	emit('close')
}

// 挂载时：如果传入了 text，自动显示
onMounted(() => {
	if (props.text) {
		currentText.value = props.text
		currentType.value = props.type
		visible.value = true
		startTimer(props.duration)
	}
})

onBeforeUnmount(() => clearTimer())

defineExpose({ show, hide })
</script>

<style lang="scss" scoped>
.inline-message-fade-enter-active {
	transition:
		opacity 0.25s ease,
		transform 0.25s ease;
}

.inline-message-fade-leave-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}

.inline-message-fade-enter-from {
	opacity: 0;
	transform: scale(0.85);
}

.inline-message-fade-leave-to {
	opacity: 0;
	transform: scale(0.9);
}

.inline-message-box {
	max-width: 80vw;
	height: 56px;
	display: flex;
	align-items: center;
	padding: 16px 24px;
	gap: 8px;
	border-radius: 16px;
}
</style>
